// src/utils/advisoryReportPdf.js
//
// Parent-facing PDF report generator for the Advisory Dashboard.
// King's Academy Faculty Tool Kit
//
// Imperative jsPDF rendering (draw text/rects/lines directly) — NOT
// doc.html()/html2canvas, which produces blank pages in this app. Mirrors the
// Department Review / checkout PDF conventions.
//
// PUBLIC API:
//   buildAdvisoryReport(students, { sections, advisorName, range })
//     students: array of advisee row objects (from getAdvisoryAttendance).
//       Pass one student for a single report, or many for a batch (one
//       student per page). Each: { name, personId, grade, cohort, courses,
//       gradesAsOf, events, <bucketKey counts> }.
//     sections: { reportCard, ungraded, attendanceSummary, attendanceDetail }
//       booleans — which optional sections to include. Header/identity + the
//       "grades as of" date are ALWAYS included.
//     buckets: the ordered [{key,label,color}] from the dashboard, so
//       attendance summary labels match config.
//   Returns a jsPDF instance; caller does .save(filename).
//
// Requires: npm install jspdf   (already a dependency for other PDF tools)

import { jsPDF } from 'jspdf';

// ── KA brand ────────────────────────────────────────────────────────────────
const NAVY = [35, 60, 95];
const RED = [232, 53, 48];
const GOLD = [162, 145, 100];
const INK = [26, 26, 26];
const MUTED = [107, 114, 128];
const LINE = [225, 227, 231];

// Grade band colors (match the dashboard).
const BAND = {
  green: [46, 125, 50],
  blue: [35, 60, 95],
  orange: [232, 131, 12],
  red: [232, 53, 48],
  gray: [156, 163, 175],
};
function bandFor(score) {
  if (score == null || Number.isNaN(Number(score))) return 'gray';
  const n = Number(score);
  if (n >= 90) return 'green';
  if (n >= 80) return 'blue';
  if (n >= 70) return 'orange';
  return 'red';
}

// Parent-friendly relabeling of internal attendance bucket labels (choice B).
// Falls back to the raw label if we don't have a friendlier version.
const FRIENDLY_LABELS = {
  'Class absence': 'Class absences',
  'Advisory absence': 'Advisory absences',
  'Coco absence': 'Co-curricular absences',
  'Lunch absence': 'Lunch absences',
  'Unexcused absence': 'Unexcused absences',
  'Excused absence': 'Excused absences',
  'Unexcused tardy': 'Unexcused tardies',
  'Excused tardy': 'Excused tardies',
};
function friendly(label) {
  return FRIENDLY_LABELS[label] || label;
}

// ── date helpers ──────────────────────────────────────────────────────────
function prettyDate(key) {
  if (!key) return null;
  const d = new Date(String(key).slice(0, 10) + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return String(key);
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}
function prettyShort(key) {
  if (!key) return '—';
  const d = new Date(String(key).slice(0, 10) + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return String(key);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── layout constants (A4 portrait, mm) ──────────────────────────────────────
const PAGE = { w: 210, h: 297 };
const M = { left: 18, right: 18, top: 16, bottom: 18 };
const CONTENT_W = PAGE.w - M.left - M.right;

/**
 * Render one student onto the current page of `doc`, starting at top margin.
 * Advances an internal y cursor; adds pages if a section overflows.
 */
function renderStudent(doc, student, { sections, advisorName, range, buckets }) {
  let y = M.top;

  const setFill = (rgb) => doc.setFillColor(rgb[0], rgb[1], rgb[2]);
  const setText = (rgb) => doc.setTextColor(rgb[0], rgb[1], rgb[2]);
  const setDraw = (rgb) => doc.setDrawColor(rgb[0], rgb[1], rgb[2]);

  // Page-break helper: if the next block of `need` mm won't fit, new page.
  const ensure = (need) => {
    if (y + need > PAGE.h - M.bottom) {
      doc.addPage();
      y = M.top;
    }
  };

  // ── Letterhead ─────────────────────────────────────────────────────────
  setFill(NAVY);
  doc.rect(0, 0, PAGE.w, 26, 'F');
  setText([255, 255, 255]);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.text("King's Academy", M.left, 13);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  setText([210, 216, 226]);
  doc.text('Madaba, Jordan · Advisory Report', M.left, 20);
  y = 34;

  // ── Identity block (ALWAYS) ──────────────────────────────────────────────
  setText(NAVY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.text(student.name || `Student ${student.personId}`, M.left, y);
  y += 6;

  setText(MUTED);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  const idBits = [
    student.grade ? String(student.grade) : null,
    student.cohort ? String(student.cohort) : null,
    student.personId ? `ID ${student.personId}` : null,
  ].filter(Boolean);
  doc.text(idBits.join('   ·   '), M.left, y);
  y += 5;

  const rangeStr =
    range && range.startDate && range.endDate
      ? `${prettyShort(range.startDate)} – ${prettyShort(range.endDate)}`
      : null;
  const metaLine = [
    advisorName ? `Advisor: ${advisorName}` : null,
    rangeStr ? `Period: ${rangeStr}` : null,
    `Generated: ${prettyShort(new Date().toISOString().slice(0, 10))}`,
  ].filter(Boolean);
  doc.text(metaLine.join('   ·   '), M.left, y);
  y += 4;

  setDraw(GOLD);
  doc.setLineWidth(0.6);
  doc.line(M.left, y, M.left + CONTENT_W, y);
  y += 8;

  // ── Report card ──────────────────────────────────────────────────────────
  if (sections.reportCard) {
    const list = Array.isArray(student.courses) ? student.courses : [];
    const graded = list
      .filter((c) => c && c.currentScore != null && !Number.isNaN(Number(c.currentScore)))
      .sort((a, b) => Number(a.currentScore) - Number(b.currentScore));

    ensure(14);
    setText(NAVY);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Report Card', M.left, y);
    y += 5;

    // Prominent "as of" date — essential so a later reader knows it's a snapshot.
    setText(RED);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    const asOf = prettyDate(student.gradesAsOf);
    doc.text(asOf ? `Grades as of ${asOf}` : 'Grades: date unavailable', M.left, y);
    y += 4;

    // Color key.
    setText(MUTED);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('Key:  90+ excellent   ·   80–89 good   ·   70–79 needs attention   ·   below 70 concern', M.left, y);
    y += 5;

    if (graded.length === 0) {
      setText(MUTED);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9.5);
      doc.text('No graded courses on file for this period.', M.left, y);
      y += 6;
    } else {
      // Table: course | score, with a colored score chip.
      const rowH = 8;
      graded.forEach((c) => {
        ensure(rowH + 2);
        // row separator
        setDraw(LINE);
        doc.setLineWidth(0.2);
        doc.line(M.left, y + rowH - 2.5, M.left + CONTENT_W, y + rowH - 2.5);

        setText(INK);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        const name = c.courseName || c.courseCode || 'Course';
        doc.text(name.length > 70 ? name.slice(0, 68) + '…' : name, M.left, y + 2);

        // score chip
        const band = BAND[bandFor(c.currentScore)];
        const scoreStr = String(Math.round(Number(c.currentScore)));
        const chipW = 16;
        const chipX = M.left + CONTENT_W - chipW;
        setFill(band);
        doc.roundedRect(chipX, y - 2.6, chipW, 6.4, 1.4, 1.4, 'F');
        setText([255, 255, 255]);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.text(scoreStr, chipX + chipW / 2, y + 2, { align: 'center' });

        y += rowH;
      });
      y += 2;
    }

    y += 3;
  }

  // ── Courses without grades (opt-in, independent of the report card) ────────
  if (sections.ungraded) {
    const list = Array.isArray(student.courses) ? student.courses : [];
    const ungraded = list.filter(
      (c) => !c || c.currentScore == null || Number.isNaN(Number(c.currentScore))
    );

    ensure(12);
    setText(NAVY);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Courses Without Grades', M.left, y);
    y += 6;

    if (ungraded.length === 0) {
      setText(MUTED);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9.5);
      doc.text('None — every course on file has a grade.', M.left, y);
      y += 6;
    } else {
      setText(MUTED);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      const names = ungraded.map((c) => (c && (c.courseName || c.courseCode)) || 'Course');
      const wrapped = doc.splitTextToSize(names.join('   ·   '), CONTENT_W);
      ensure(wrapped.length * 4.8 + 2);
      doc.text(wrapped, M.left, y);
      y += wrapped.length * 4.8 + 4;
    }

    y += 3;
  }

  // ── Attendance summary (parent-friendly labels) ──────────────────────────
  if (sections.attendanceSummary) {
    const cols = Array.isArray(buckets) ? buckets : [];
    // Only show categories the student actually has counts in (keeps it clean).
    const present = cols
      .map((b) => ({ label: friendly(b.label), count: Number(student[b.key] || 0), color: b.color }))
      .filter((r) => r.count > 0);

    ensure(14);
    setText(NAVY);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Attendance Summary', M.left, y);
    y += 3;
    setText(MUTED);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const rangeNote = rangeStr ? `For ${rangeStr}.` : '';
    doc.text(`Counts of missed obligations this period. ${rangeNote}`, M.left, y + 3);
    y += 8;

    if (present.length === 0) {
      setText([46, 125, 50]);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      doc.text('No absences or tardies recorded this period — a clean record.', M.left, y);
      y += 7;
    } else {
      const rowH = 7;
      present.forEach((r) => {
        ensure(rowH);
        setText(INK);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.text(r.label, M.left, y + 1.5);
        doc.setFont('helvetica', 'bold');
        doc.text(String(r.count), M.left + CONTENT_W, y + 1.5, { align: 'right' });
        setDraw(LINE);
        doc.setLineWidth(0.2);
        doc.line(M.left, y + rowH - 2, M.left + CONTENT_W, y + rowH - 2);
        y += rowH;
      });
      y += 3;
    }
  }

  // ── Attendance detail ────────────────────────────────────────────────────
  if (sections.attendanceDetail) {
    const events = Array.isArray(student.events) ? student.events : [];
    // Map bucketKey -> friendly label via buckets.
    const keyToLabel = {};
    (buckets || []).forEach((b) => (keyToLabel[b.key] = friendly(b.label)));

    ensure(12);
    setText(NAVY);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Attendance Detail', M.left, y);
    y += 6;

    if (events.length === 0) {
      setText(MUTED);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9.5);
      doc.text('No individual absence or tardy records this period.', M.left, y);
      y += 6;
    } else {
      // header row
      setText(MUTED);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.text('DATE', M.left, y);
      doc.text('TYPE', M.left + 34, y);
      doc.text('BLOCK', M.left + 84, y);
      doc.text('CLASS', M.left + 104, y);
      y += 2;
      setDraw(LINE);
      doc.setLineWidth(0.3);
      doc.line(M.left, y, M.left + CONTENT_W, y);
      y += 4;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      setText(INK);
      events.forEach((e) => {
        ensure(6);
        doc.text(prettyShort(e.date), M.left, y);
        const typeLabel = e.bucketKey ? keyToLabel[e.bucketKey] || e.bucketKey : 'Other';
        doc.text(typeLabel.length > 26 ? typeLabel.slice(0, 24) + '…' : typeLabel, M.left + 34, y);
        doc.text(String(e.block || '—'), M.left + 84, y);
        const cls = String(e.class || '—');
        doc.text(cls.length > 42 ? cls.slice(0, 40) + '…' : cls, M.left + 104, y);
        y += 5.5;
      });
      y += 2;
    }
  }
}

// ── Page footer on every page ────────────────────────────────────────────────
function stampFooters(doc) {
  const n = doc.getNumberOfPages();
  for (let i = 1; i <= n; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
    doc.text(
      "King's Academy · Advisory Report · Confidential",
      M.left,
      PAGE.h - 8
    );
    doc.text(`Page ${i} of ${n}`, PAGE.w - M.right, PAGE.h - 8, { align: 'right' });
  }
}

/**
 * Build a report PDF for one or many students. Returns the jsPDF instance.
 */
export function buildAdvisoryReport(students, opts = {}) {
  const list = Array.isArray(students) ? students : [students];
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  list.forEach((student, idx) => {
    if (idx > 0) doc.addPage();
    renderStudent(doc, student, opts);
  });

  stampFooters(doc);
  return doc;
}